/**
 * BOE パッケージパターン通知設定 — Frontend ローカル Contract
 *
 * BE 側 `dresscode-backend/v2/src/shared-kernel/api/operation-engine/boe-package-notification-config/boe-package-notification-config.api-contract.ts`
 * （SSoT）と同じ Zod 構造を再現。
 *
 * `@standandforce/api-contract` の publish 版（0.0.652 時点）は `content` フィールド
 * 追加前・`templateKey` を入力に要求する旧構造のため、新構造が publish された後に
 * client.ts でローカル import をパッケージ import に置き換える想定。
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';
const OwnerProductSchema = z.enum(['IT_OPERATION_CORE', 'HRF_LABOR_CORE', 'HRF_CONTRACTOR_CORE', 'GA_OPERATION_CORE']);
const NotificationConfigTriggerTypeSchema = z.enum(['Arrive', 'Leave']);
export const NodeNotificationTriggerSchema = z.enum(['Arrive', 'Leave']);
const TransitionNotificationTriggerSchema = z.enum(['Arrive', 'Leave']);
const PackagePatternPathSchema = z.object({
  ownerProduct: OwnerProductSchema,
  packagePatternId: z.string().min(1)
});

// 通知文言（content）。BOE はチャット系主体で subject を持たないことが多い。
const NotificationContentSchema = z.object({
  body: z.string().min(1),
  subject: z.string().nullable().optional()
});
const NotificationConfigItemSchema = z.object({
  id: z.string(),
  trigger: NotificationConfigTriggerTypeSchema,
  // BE がドメイン採番する内部キー。FE は表示・編集・送信しない
  templateKey: z.string(),
  recipientVocabularies: z.array(z.string()),
  // 文言。旧データ等で未作成なら null。BFF 経由の保存後は常に存在する
  content: NotificationContentSchema.nullable()
});
const PatternNodeNotificationAttachmentSchema = z.object({
  nodeId: z.string(),
  nodeType: z.enum(['Task', 'Event', 'CallPackage']),
  eventType: z.enum(['Start', 'End']).nullable(),
  settings: z.array(NotificationConfigItemSchema)
});
const PatternTransitionNotificationAttachmentSchema = z.object({
  transitionId: z.string(),
  fromNodeId: z.string(),
  toNodeId: z.string(),
  settings: z.array(NotificationConfigItemSchema)
});
const ListPatternNotificationConfigsOutputSchema = z.object({
  packagePatternId: z.string(),
  nodes: z.array(PatternNodeNotificationAttachmentSchema),
  transitions: z.array(PatternTransitionNotificationAttachmentSchema)
});
const NotificationVariableSchema = z.object({
  token: z.string(),
  label: z.string(),
  type: z.string(),
  sourceCatalogCode: z.string()
});
const ListPatternNotificationVariablesOutputSchema = z.object({
  packagePatternId: z.string(),
  variables: z.array(NotificationVariableSchema)
});
const PutNotificationConfigOutputSchema = NotificationConfigItemSchema;
const DeleteNotificationConfigOutputSchema = z.object({
  deletedSettingIds: z.array(z.string())
});
const ListPatternNotificationConfigsInputSchema = PackagePatternPathSchema;
const DeleteNodeNotificationConfigInputSchema = PackagePatternPathSchema.extend({
  nodeId: z.string().min(1)
});
const DeleteTransitionNotificationConfigInputSchema = PackagePatternPathSchema.extend({
  transitionId: z.string().min(1)
});

// 入力（PUT）schema は BE 側で `.strict()`。未知キー（templateKey / channelConnectionId 等）
// を送ると 400 になるため、ここでも `.strict()` を維持する。
const PutNodeNotificationConfigInputSchema = PackagePatternPathSchema.extend({
  nodeId: z.string().min(1),
  trigger: NodeNotificationTriggerSchema,
  recipientVocabularies: z.array(z.string().min(1)).min(1),
  content: NotificationContentSchema
}).strict();
const PutTransitionNotificationConfigInputSchema = PackagePatternPathSchema.extend({
  transitionId: z.string().min(1),
  trigger: TransitionNotificationTriggerSchema,
  recipientVocabularies: z.array(z.string().min(1)).min(1),
  content: NotificationContentSchema
}).strict();
const notificationsBasePath = '/v2/boe/package/{ownerProduct}/configs/patterns/{packagePatternId}/notifications';
export const boePackageNotificationConfigContract = oc.router({
  list: oc.route({
    method: 'GET',
    path: notificationsBasePath
  }).input(ListPatternNotificationConfigsInputSchema).output(ListPatternNotificationConfigsOutputSchema),
  variables: oc.route({
    method: 'GET',
    path: `${notificationsBasePath}/variables`
  }).input(ListPatternNotificationConfigsInputSchema).output(ListPatternNotificationVariablesOutputSchema),
  putNode: oc.route({
    method: 'PUT',
    path: `${notificationsBasePath}/nodes/{nodeId}`
  }).input(PutNodeNotificationConfigInputSchema).output(PutNotificationConfigOutputSchema),
  deleteNode: oc.route({
    method: 'DELETE',
    path: `${notificationsBasePath}/nodes/{nodeId}`
  }).input(DeleteNodeNotificationConfigInputSchema).output(DeleteNotificationConfigOutputSchema),
  putTransition: oc.route({
    method: 'PUT',
    path: `${notificationsBasePath}/transitions/{transitionId}`
  }).input(PutTransitionNotificationConfigInputSchema).output(PutNotificationConfigOutputSchema),
  deleteTransition: oc.route({
    method: 'DELETE',
    path: `${notificationsBasePath}/transitions/{transitionId}`
  }).input(DeleteTransitionNotificationConfigInputSchema).output(DeleteNotificationConfigOutputSchema)
});
export type NotificationConfigTrigger = z.infer<typeof NotificationConfigTriggerTypeSchema>;
export type NotificationConfigItem = z.infer<typeof NotificationConfigItemSchema>;
export type NotificationVariable = z.infer<typeof NotificationVariableSchema>;
export type ListPatternNotificationConfigsOutput = z.infer<typeof ListPatternNotificationConfigsOutputSchema>;
export type ListPatternNotificationVariablesOutput = z.infer<typeof ListPatternNotificationVariablesOutputSchema>;
export type PutNodeNotificationConfigInput = z.infer<typeof PutNodeNotificationConfigInputSchema>;